"use client";

import { type ReactNode, useEffect, useState } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { Icon } from "@/components/ui/Icon";
import { getSiteContent } from "@/content";
import { type Locale, localizedPath, switchLocalePath } from "@/lib/site";

export function SiteChrome({ locale, children }: { locale: Locale; children: ReactNode }) {
  const pathname = usePathname();
  const [menuOpen, setMenuOpen] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [headerReady, setHeaderReady] = useState(false);
  const content = getSiteContent(locale);
  const navGroups = content.chrome.nav;
  const targetLocale: Locale = locale === "zh" ? "en" : "zh";
  const resolvedPathname = pathname || `/${locale}/`;
  const isHome = resolvedPathname === `/${locale}/`;
  const hideMobileContact = /\/(login|register)\/?$/.test(resolvedPathname);
  const currentSlug = resolvedPathname.replace(/^\/(zh|en)\/?/, "").replace(/\/$/, "");
  const isActivePath = (path: string) => (path === "" ? currentSlug === "" : currentSlug === path);

  useEffect(() => {
    setHeaderReady(true);
    const onScroll = () => setScrolled(window.scrollY > 50);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.classList.toggle("menu-locked", menuOpen);
    return () => document.body.classList.remove("menu-locked");
  }, [menuOpen]);

  const switchPath = switchLocalePath(locale, targetLocale, resolvedPathname);

  return (
    <>
      <header className={`site-header ${headerReady ? "header-ready" : ""} ${isHome ? "header-home" : "header-solid"} ${isHome && scrolled ? "is-scrolled" : ""}`}>
        <div className="header-inner">
          <Link className="brand" href={localizedPath(locale)} aria-label={content.chrome.brandAria}>
            <img src="/assets/logo/orcaparcel-logo.png" alt={content.chrome.logoAlt} width={1600} height={400} fetchPriority="high" />
          </Link>
          <nav className="desktop-nav" aria-label={content.chrome.navAria}>
            {navGroups.map((item) =>
              "children" in item ? (
                <div className="nav-dropdown" key={item.label}>
                  <button className={item.children.some((child) => isActivePath(child.path)) ? "is-active" : undefined} type="button">
                    {item.label} <span className="material-symbols-outlined text-sm">expand_more</span>
                  </button>
                  <div className="dropdown-panel">
                    {item.children.map((child) => (
                      <Link key={child.path} href={localizedPath(locale, child.path)}>
                        {child.label}
                      </Link>
                    ))}
                  </div>
                </div>
              ) : (
                <Link className={isActivePath(item.path) ? "is-active" : undefined} key={item.path} href={localizedPath(locale, item.path)}>
                  {item.label}
                </Link>
              )
            )}
          </nav>
          <div className="header-actions">
            <div className="language-switcher">
              <button className="language-link" type="button">
                <span className="material-symbols-outlined">language</span>
                <span>{locale === "en" ? content.chrome.language.en : content.chrome.language.current}</span>
                <span className="material-symbols-outlined text-sm">expand_more</span>
              </button>
              <div className="language-menu">
                <Link href={localizedPath("zh", resolvedPathname.replace(/^\/(zh|en)\/?/, ""))}>{content.chrome.language.zh}</Link>
                <Link href={switchPath}>{content.chrome.language.en}</Link>
              </div>
            </div>
            <Link className="login-link" href={localizedPath(locale, "login")}>
              {content.chrome.actions.login}
            </Link>
            <Link className="register-link" href={localizedPath(locale, "register")}>
              {content.chrome.actions.register}
            </Link>
            <button className="menu-toggle" type="button" onClick={() => setMenuOpen(true)} aria-label={content.chrome.actions.openMenu}>
              <Icon name="menu" />
            </button>
          </div>
        </div>
      </header>

      <div className={`mobile-menu ${menuOpen ? "is-open" : ""}`} aria-hidden={!menuOpen}>
        <div className="mobile-menu-top">
          <Link className="brand brand-light" href={localizedPath(locale)}>
            <img src="/assets/logo/orcaparcel-logo.png" alt={content.chrome.logoAlt} width={1600} height={400} />
          </Link>
          <button className="menu-close" type="button" onClick={() => setMenuOpen(false)} aria-label={content.chrome.actions.closeMenu}>
            <Icon name="close" />
          </button>
        </div>
        <nav className="mobile-menu-nav" aria-label={content.chrome.mobileNavAria}>
          {navGroups.map((item) =>
            "children" in item ? (
              <section key={item.label}>
                <h3>{item.label}</h3>
                {item.children.map((child) => (
                  <Link key={child.path} href={localizedPath(locale, child.path)} onClick={() => setMenuOpen(false)}>
                    {child.label}
                  </Link>
                ))}
              </section>
            ) : (
              <section key={item.path}>
                <Link href={localizedPath(locale, item.path)} onClick={() => setMenuOpen(false)}>
                  {item.label}
                </Link>
              </section>
            )
          )}
        </nav>
        <div className="mobile-menu-actions">
          <Link href={switchPath}>{targetLocale === "en" ? content.chrome.language.mobileSwitchToEn : content.chrome.language.mobileSwitchToZh}</Link>
          <Link href={localizedPath(locale, "login")}>{content.chrome.actions.login}</Link>
          <Link className="mobile-register" href={localizedPath(locale, "register")}>
            {content.chrome.actions.register}
          </Link>
        </div>
      </div>

      <main id="main" className={hideMobileContact ? "has-mobile-bottom-bar-safe-area mobile-contact-hidden-page" : "has-mobile-bottom-bar-safe-area"}>
        {children}
      </main>
      <Footer locale={locale} pathname={resolvedPathname} />
      <FloatingContact locale={locale} />
      <MobileContact locale={locale} contactOpen={contactOpen} setContactOpen={setContactOpen} menuOpen={menuOpen || hideMobileContact} />
    </>
  );
}

function Footer({ locale, pathname }: { locale: Locale; pathname: string }) {
  const content = getSiteContent(locale).chrome.footer;
  const zhPath = switchLocalePath(locale, "zh", pathname);
  const enPath = switchLocalePath(locale, "en", pathname);

  return (
    <footer className="site-footer">
      <div className="footer-inner">
        <div className="footer-grid">
          <div className="footer-brand">
            <Link className="footer-logo" href={localizedPath(locale)} aria-label={content.brandAria}>
              <img src="/assets/logo/orcaparcel-logo.png" alt={getSiteContent(locale).chrome.logoAlt} width={1600} height={400} />
            </Link>
            <p>{content.brandCopy}</p>
            <div className="footer-tags" aria-label={content.serviceTagsAria}>
              {content.serviceTags.map((tag) => (
                <span key={tag}>{tag}</span>
              ))}
            </div>
          </div>
          {content.columns.map((column) => (
            <FooterColumn title={column.title} items={column.items} locale={locale} key={column.title} />
          ))}
        </div>
        <div className="footer-bottom">
          <span>{content.copyright}</span>
          <div className="footer-bottom-links">
            {content.legalLinks.map(([label, path], index) => (
              <span className="footer-legal-link-item" key={label}>
                {index > 0 ? <span className="footer-link-divider" aria-hidden="true">｜</span> : null}
                <Link href={localizedPath(locale, path)}>{label}</Link>
              </span>
            ))}
            <span>{content.filingPlaceholder}</span>
          </div>
          <div className="footer-language-links" aria-label={getSiteContent(locale).chrome.language.aria}>
            <Link href={zhPath}>{getSiteContent(locale).chrome.language.mobileSwitchToZh}</Link>
            <span> / </span>
            <Link href={enPath}>{getSiteContent(locale).chrome.language.en}</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}

function FooterColumn({ title, items, locale }: { title: string; items: readonly (readonly [string, string])[]; locale: Locale }) {
  return (
    <div className="footer-column">
      <h3>{title}</h3>
      {items.map(([label, path]) => (
        <Link key={`${title}-${label}`} href={path === "#" ? "#" : localizedPath(locale, path)}>
          {label}
        </Link>
      ))}
    </div>
  );
}

function FloatingContact({ locale }: { locale: Locale }) {
  const content = getSiteContent(locale).chrome.floatingContact;
  return (
    <aside className="floating-contact" aria-label={content.aria}>
      <Link className="floating-contact-qr" href={localizedPath(locale, "contact")} title={content.wechat}>
        <span className="material-symbols-outlined">chat</span>
        <span className="floating-qr-panel">
          <span className="floating-qr-box">
            <span className="material-symbols-outlined">qr_code_2</span>
          </span>
          <span>{content.qrText}</span>
        </span>
      </Link>
      <Link href={localizedPath(locale, "contact")} title={content.qq}>
        <span className="material-symbols-outlined">chat_bubble</span>
      </Link>
      <Link href={localizedPath(locale, "contact")} title={content.online}>
        <span className="material-symbols-outlined">forum</span>
      </Link>
      <button className="floating-top" type="button" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} title={content.backTop}>
        <span className="material-symbols-outlined">arrow_upward</span>
      </button>
    </aside>
  );
}

function MobileContact({
  locale,
  contactOpen,
  setContactOpen,
  menuOpen
}: {
  locale: Locale;
  contactOpen: boolean;
  setContactOpen: (open: boolean) => void;
  menuOpen: boolean;
}) {
  const content = getSiteContent(locale).chrome.mobileContact;
  return (
    <>
      <nav className={`mobile-bottom-bar ${menuOpen ? "is-hidden" : ""}`} aria-label={content.aria}>
        <Link href={localizedPath(locale, "contact")}>{content.primary[0]}</Link>
        <Link href={localizedPath(locale, "contact")}>{content.primary[1]}</Link>
        <a href="tel:4001886888">{content.primary[2]}</a>
        <button type="button" onClick={() => setContactOpen(true)}>
          {content.more}
        </button>
      </nav>
      <div className={`mobile-contact-sheet ${contactOpen ? "is-open" : ""}`} aria-hidden={!contactOpen}>
        <button type="button" onClick={() => setContactOpen(false)}>
          <Icon name="close" />
          {content.close}
        </button>
        <Link href={localizedPath(locale, "contact")}>{content.sheet[0]}</Link>
        <Link href={localizedPath(locale, "contact")}>{content.sheet[1]}</Link>
        <Link href={localizedPath(locale, "contact")}>{content.sheet[2]}</Link>
        <a href="mailto:support@orcaparcel.com">{content.sheet[3]}</a>
        <Link href={localizedPath(locale, "contact")}>{content.sheet[4]}</Link>
      </div>
    </>
  );
}
